import { animate, style, transition, trigger } from '@angular/animations';
import moment from 'moment';
import * as tz from 'moment-timezone';
import {
  BsDatepickerConfig,
  BsDaterangepickerConfig,
} from 'ngx-bootstrap/datepicker';
import { TimepickerConfig } from 'ngx-bootstrap/timepicker';
import {
  Company,
  FinancialMovementType,
  FinancialState,
} from '../enums/financial.enum';
import {
  ApexChart,
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexLegend,
  ApexPlotOptions,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexStroke,
  ApexDataLabels,
  ApexTooltip,
  ApexGrid,
  ApexFill,
  ApexMarkers,
} from 'ng-apexcharts';
import { WizardStep } from '../interfaces/ui/create-movement-modal.interface';
import { MovementFilters } from '../interfaces/ui/movement-filters.interface';
import { FinancialStats } from '../interfaces/ui/financial-stats.interface';
import { BonusTypeDashboard } from '../interfaces/api/bonus-type.interface';
import {
  AgentPermissionFilter,
  AgentPermissionSortValue,
  AssignedSortValue,
  PercentageSortValue,
  StatusFilter,
  StatusSortValue,
} from '../types/ui/bonus-type-management.types';
import { FilterSelectOption } from '../interfaces/bonus-type-management.interface';

export const AVAILABLE_LANGUAGES = {
  ES: 'es',
  EN: 'us',
  PT: 'pt',
};

export const AVAILABLE_THEME = {
  DARK: 'dark-only',
  WHITE: '',
};

export const AGENT_ID_DEFAULT = 0;

export const TIME_OUT = {
  /*
  8 is the number of hours of inactivity before locking the screen
  60 * 60 is the number of seconds in an hour.
  1000 is the number of ms in a second
*/
  TIME: 8 * 60 * 60 * 1000,
};

export const userOnlyActive = 1;

export const userActiveAndInactive = undefined;

export const agentAfiliateAccountant = [3, 4, 5];

export const DATE_PICKER_RANGE_LABELS = {
  TODAY: 'datepicker.range.Today',
  YESTERDAY: 'datepicker.range.Yesterday',
  LAST_7_DAYS: 'datepicker.range.Last7Days',
  LAST_30_DAYS: 'datepicker.range.Last30Days',
  THIS_MONTH: 'datepicker.range.ThisMonth',
  LAST_MONTH: 'datepicker.range.LastMonth',
};
export const CODE_BONUS_CONSTANS = {
  ATC: 1,
  PAGOS: 2,
  ADMINISTRADOR: 3,
  AGENTE: 4,
  AFILIADO: 5,
  PAGE: 1,
  LIMIT_TABLE: 10,
};
export const defaultMinDatepickerV1 = '2020-01-02';

export const defaultInitialDateRangepickerV1 = [
  moment().subtract(1, 'years').startOf('day').toDate(),
  moment().add(1, 'days').startOf('day').toDate(),
];
export const defaultInitialDateRangepickerV2 = [
  moment().subtract(1, 'month').startOf('month').toDate(),
  moment().subtract(1, 'month').endOf('month').toDate(),
];

export const defaultInitialDateRangepickerV3 = [
  moment().startOf('month').toDate(),
  moment().endOf('month').toDate(),
];

export const defaultRangesDatepickerV1 = [
  {
    value: [
      moment().startOf('day').toDate(),
      moment().add(1, 'days').startOf('day').toDate(),
    ],
    label: 'Hoy',
  },
  {
    value: [
      moment().subtract(1, 'days').startOf('day').toDate(),
      moment().startOf('day').toDate(),
    ],
    label: 'Ayer',
  },
  {
    value: [
      moment().subtract(6, 'days').startOf('day').toDate(),
      moment().add(1, 'days').startOf('day').toDate(),
    ],
    label: 'Ultimos 7 Días',
  },
  {
    value: [
      moment().subtract(29, 'days').startOf('day').toDate(),
      moment().add(1, 'days').startOf('day').toDate(),
    ],
    label: 'Ultimos 30 Días',
  },
  {
    value: [
      moment().startOf('month').toDate(),
      moment().endOf('month').toDate(),
    ],
    label: 'Este Mes',
  },
  {
    value: [
      moment().subtract(1, 'month').startOf('month').toDate(),
      moment().subtract(1, 'month').endOf('month').toDate(),
    ],
    label: 'Mes Pasado',
  },
  {
    value: [
      moment().subtract(3, 'months').startOf('day').toDate(),
      moment().add(1, 'days').startOf('day').toDate(),
    ],
    label: 'Ultimos 3 Meses',
  },
];

export const CURRENCY_BY_COUNTRY_CODE: Record<
  string,
  { currency: string; locale: string; symbol: string }
> = {
  EC: { currency: 'USD', locale: 'es-EC', symbol: '$' },
  PE: { currency: 'PEN', locale: 'es-PE', symbol: 'S/' },
};
export const COUNTRY_CODES_DEFAULT = {
  EC: 'EC',
  PE: 'PE',
};

/**
 * Highest page size the API will honor. The backend clamps any `limit` above
 * this value, so offering larger options makes the pagination totals lie.
 * Keep in sync with MAX_LIMIT in back-admin-nodejs clamp-pagination middleware.
 */
export const MAX_API_PAGE_SIZE = 200;

export const DAFAULT_PAGINATION_OPTIONS = [10, 20, 50, 100, MAX_API_PAGE_SIZE];
export const MAX_FINANCIAL_RECORDS_LIMIT = 1000;

export const CONFIG_BASE_DATE_RANGEPICKER: {
  minDate: Date;
  maxDate: Date;
  initialRange: Date[];
  bsConfig: Partial<BsDaterangepickerConfig>;
} = {
  minDate: moment('2020-01-01 00:00:00').toDate(),
  maxDate: moment().add(1, 'days').startOf('day').toDate(),
  initialRange: [
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate(),
  ],
  bsConfig: {
    minMode: 'day',
    keepDatesOutOfRules: true,
    containerClass: 'theme-blue',
    showPreviousMonth: true,
    ranges: defaultRangesDatepickerV1,
    rangeInputFormat: 'DD/MM/YYYY',
    dateInputFormat: 'DD/MM/YYYY',
    preventChangeToNextMonth: true,
  },
};

export const CONFIG_BASE_DATE_PICKER: {
  minDate: Date;
  maxDate: Date;
  currentDate: Date;
  bsConfig: Partial<BsDatepickerConfig>;
} = {
  minDate: new Date(2020, 0, 1),
  maxDate: new Date(2100, 11, 31),
  currentDate: tz.tz('America/Guayaquil').toDate(),
  bsConfig: {
    dateInputFormat: 'DD/MM/YYYY',
    showWeekNumbers: false,
    adaptivePosition: true,
  },
};

export const ALLOW_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

export const ACTION_SOCKET = {
  ADD: 'A',
  UPDATE: 'U',
  DELETE: 'D',
};

export const GENERAL_STATUS = {
  ACTIVE: 1,
  INACTIVE: 0,
};

export const BONUS_COLOR_TYPE = [
      '#4CAF50', '#2196F3', '#FF9800',
      '#9C27B0', '#F44336', '#00BCD4',
      '#795548', '#607D8B', '#E91E63',
    ]

export const DEFAULT_NUMBERS_FLAGS = {
  ACTIVE: 1,
  INACTIVE: 0,
}

export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

export const NG_SELECT_TYPE = {
  COUNTRY: 'country',
  AGENT: 'agent',
  BANK: 'bank',
  CURRENCY: 'currency',
  HOLDER: 'holder',
  GENDER: 'gender',
  LANGUAGE: 'language',
  CITY: 'city',
  BONUS: 'bonus',
};
export const MOVEMENT_STATUS = {
  DEBIT: 1,
  CREDIT: 2,
};

export const CONFIG_BASE_TIME_PICKER: TimepickerConfig = {
  hourStep: 1,
  minuteStep: 1,
  showMeridian: false,
  readonlyInput: false,
  disabled: false,
  secondsStep: 1,
  mousewheel: true,
  allowEmptyTime: false,
  arrowkeys: true,
  showSpinners: true,
  showSeconds: false,
  meridians: ['AM', 'PM'],
  showMinutes: true,
  hoursPlaceholder: 'HH',
  minutesPlaceholder: 'MM',
  secondsPlaceholder: 'SS',
  ariaLabelHours: 'Hours',
  ariaLabelMinutes: 'Minutes',
  ariaLabelSeconds: 'Seconds',
};

export const RRHH_STATE = {
  PENDING: 30,
  APPROVED: 31,
  REJECTED: 32,
};

export const OPTIONS_SECURITY = {
  password: 'password',
  pin: 'pin',
  cardKey: 'cardKey',
  token: 'token',
};

export const EDITOR_CONFIG = {
  placeholder: 'Redacte aquí...',
  toolbar: ['bold', 'italic', 'numberedList', 'link', 'undo', 'redo'],
};

export const PAGINATION = {
  TOTAL: 0,
  PAGE: 1,
  LIMIT: 10,
  LIMIT_2: 100,
};

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, MAX_API_PAGE_SIZE];

export const DEFAULT_DASHBOARD: BonusTypeDashboard = {
    activeBonusTypes: 0,
    agentsWithAssignments: 0,
    visibleForAgents: 0,
    totalActivePercentage: 0,
  };

export const AGENT_PERMISSION_OPTIONS: ReadonlyArray<
  FilterSelectOption<AgentPermissionFilter>
> = [
  { label: 'Todos', value: '' },
  { label: 'Visible', value: '1' },
  { label: 'Oculto', value: '0' },
];

export const STATUS_OPTIONS: ReadonlyArray<
  FilterSelectOption<StatusFilter>
> = [
  { label: 'Todos', value: '' },
  { label: 'Activo', value: '1' },
  { label: 'Inactivo', value: '0' },
];

export const STATUS_SORT = {
  NONE: '',
  ACTIVE_FIRST: 'active_first',
  INACTIVE_FIRST: 'inactive_first',
} as const satisfies Record<string, StatusSortValue>;

export const ASSIGNED_SORT = {
  NONE: '',
  ASSIGNED_FIRST: 'assigned_first',
  UNASSIGNED_FIRST: 'unassigned_first',
} as const satisfies Record<string, AssignedSortValue>;

export const PERCENTAGE_SORT = {
  NONE: '',
  HIGH_TO_LOW: 'high_to_low',
  LOW_TO_HIGH: 'low_to_high',
} as const satisfies Record<string, PercentageSortValue>;

export const AGENT_PERMISSION_SORT = {
  NONE: '',
  VISIBLE_FIRST: 'visible_first',
  HIDDEN_FIRST: 'hidden_first',
} as const satisfies Record<string, AgentPermissionSortValue>;

export const SORT_ICON = {
  ASCENDING: 'fa-arrow-up',
  DESCENDING: 'fa-arrow-down',
  NEUTRAL: 'fa-sort',
} as const;

export const SORT_LABEL_DEFAULT = 'Normal';

export const STATUS_SORT_LABELS: Record<StatusSortValue, string> = {
  '': SORT_LABEL_DEFAULT,
  active_first: 'Activos primero',
  inactive_first: 'Inactivos primero',
};

export const ASSIGNED_SORT_LABELS: Record<AssignedSortValue, string> = {
  '': SORT_LABEL_DEFAULT,
  assigned_first: 'Con agentes primero',
  unassigned_first: 'Sin agentes primero',
};

export const PERCENTAGE_SORT_LABELS: Record<PercentageSortValue, string> = {
  '': SORT_LABEL_DEFAULT,
  high_to_low: 'Mayor a menor',
  low_to_high: 'Menor a mayor',
};

export const AGENT_PERMISSION_SORT_LABELS: Record<AgentPermissionSortValue, string> = {
  '': SORT_LABEL_DEFAULT,
  visible_first: 'Agente ve primero',
  hidden_first: 'Ocultos primero',
};

export const DATE_FORMATS = {
  SHORT_DATE: 'dd/MM/yyyy',
  DATETIME: 'dd/MM/yyyy HH:mm',
  TIME: 'HH:mm',
  FULL_DATE: 'dd/MM/yyyy HH:mm:ss',
};

export const DEFAULT_VALUES = {
  NOT_AVAILABLE: 'N/A',
  UNKNOWN: 'Desconocido',
};

export const PRIMITIVE_VALUES = {
  UNDEFINED: undefined,
  NULL: null,
  TRUE: true,
  FALSE: false,
  EMPTY_STRING: '',
  ZERO: 0,
};

// ========== CONSTANTES PARA TRANSACCIONES ==========

// Tipos de transacciones simples (no requieren banco ni comprobante)
export const WITHDRAWAL_TRANSACTIONS = ['1', '4'];

// ========== CONSTANTES PARA FILE UPLOAD ==========

// Tamaño máximo de archivo (5MB)
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Tipos de archivo permitidos
export const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
];

export const ALLOWED_FILE_TYPES_USER_FORM = [
  'image/jpeg',
  'image/jpg',
  'image/png',
];

export const ALLOWED_FILE_EXTENSIONS = 'JPG, PNG, PDF';

// ========== CONSTANTES PARA BANCOS ==========

export const SUPPORTED_BANKS: string[] = [
  'pichincha',
  'bolivariano',
  'guayaquil',
  'pacifico',
  'produbanco',
  'austro',
  'internacional',
  'loja',
  'machala',
  'solidario',
  'rumiñahui',
  'jep',
];

/**
 * Niveles de confiabilidad de transacción
 */
export const CONFIDENCE_LEVELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  NONE: 'none',
} as const;

/**
 * Tipo derivado de las constantes
 */
export type ConfidenceLevel =
  (typeof CONFIDENCE_LEVELS)[keyof typeof CONFIDENCE_LEVELS];

/**
 * Umbrales de porcentaje para cada nivel
 */
export const CONFIDENCE_THRESHOLDS = {
  HIGH: 75,
  MEDIUM: 50,
  LOW: 1,
  NONE: 0,
} as const;

/**
 * Configuración visual por nivel
 */
export const CONFIDENCE_CONFIG: Record<
  ConfidenceLevel,
  {
    label: string;
    color: string;
    icon: string;
    borderColor: string;
  }
> = {
  [CONFIDENCE_LEVELS.HIGH]: {
    label: 'Alta',
    color: '#22c55e',
    icon: 'fa-shield-check',
    borderColor: '#22c55e',
  },
  [CONFIDENCE_LEVELS.MEDIUM]: {
    label: 'Media',
    color: '#f59e0b',
    icon: 'fa-shield-halved',
    borderColor: '#f59e0b',
  },
  [CONFIDENCE_LEVELS.LOW]: {
    label: 'Baja',
    color: '#f97316',
    icon: 'fa-shield-exclamation',
    borderColor: '#f97316',
  },
  [CONFIDENCE_LEVELS.NONE]: {
    label: 'Sin validar',
    color: '#ef4444',
    icon: 'fa-shield-xmark',
    borderColor: '#ef4444',
  },
};

/**
 * Determina el nivel de confiabilidad según el porcentaje
 */
export function getConfidenceLevel(percentage: number): ConfidenceLevel {
  if (percentage >= CONFIDENCE_THRESHOLDS.HIGH) return CONFIDENCE_LEVELS.HIGH;
  if (percentage >= CONFIDENCE_THRESHOLDS.MEDIUM)
    return CONFIDENCE_LEVELS.MEDIUM;
  if (percentage >= CONFIDENCE_THRESHOLDS.LOW) return CONFIDENCE_LEVELS.LOW;
  return CONFIDENCE_LEVELS.NONE;
}

// Logos de bancos
export const BANK_LOGOS: { [key: string]: string } = {
  Pichincha: 'assets/images/banks/pichincha.png',
  Pacifico: 'assets/images/banks/pacifico.png',
  Guayaquil: 'assets/images/banks/guayaquil.png',
  Jep: 'assets/images/banks/jep.png',
  Produbanco: 'assets/images/banks/produbanco.png',
  Machala: 'assets/images/banks/machala.png',
  Internacional: 'assets/images/banks/internacional.png',
  Bolivariano: 'assets/images/banks/bolivariano.png',
  Rumiñahui: 'assets/images/banks/ruminahui.png',
  Austro: 'assets/images/banks/austro.png',
  'Cooperativa Policía Nacional': 'assets/images/banks/cpn.png',
  'Banco de Loja': 'assets/images/banks/banco-loja.png',
  'Cooperativa el comercio': 'assets/images/banks/cooperativa-comercio.png',
};

export const DESTINATION_LABELS = [
  'nombre',
  'número de cuenta',
  'numero de cuenta',
  'banco',
  'correo',
];

export const VALIDATION_NAME_LABELS = [
  'nombre',
  'número',
  'numero',
  'banco',
  'correo',
  'cuenta',
  'tipo',
  'estado',
  'identificación',
  'identificacion',
];

export const DEFAULT_BANK_LOGO = 'assets/images/banks/default.png';

export const PRIORITY_BANKS = {
  ECU: [
    1, // Pichincha
    2, // Pacífico
    4, // Guayaquil
    9, // Bolivariano
    5, // Jep
    6, // Produbanco
  ],
  CL: [],
  PE: [],
};

export const MINIMUM_LOAD_AMOUNT = 5;

// ========== TIPOS DE CUENTA BANCARIA ==========

export const ACCOUNT_TYPES: { [key: number]: string } = {
  1: 'Cuenta de ahorros',
  2: 'Cuenta corriente',
  3: 'Cuenta de nómina',
};

export const BANK_STATUS = {
  INACTIVE: 0,
  ACTIVE: 1,
  INTERMITTENT: 2,
};

// ========== CONSTANTES PARA VALIDACIONES ==========

// Longitud máxima de observación
export const MAX_OBSERVATION_LENGTH = 500;

// Monto mínimo permitido
export const MIN_AMOUNT = 5;

// ========== CONSTANTES PARA UI ==========

// Tiempo de feedback al copiar (ms)
export const COPY_FEEDBACK_DURATION = 2000;

// Delay para animaciones (ms)
export const ANIMATION_DELAY = 150;

// Timeout para cambio de modal (ms)
export const MODAL_CHANGE_TIMEOUT = 300;

// ========== MENSAJES ==========

export const ERROR_MESSAGES = {
  INVALID_FILE_TYPE: `Tipo de archivo no válido. Solo se permiten ${ALLOWED_FILE_EXTENSIONS}.`,
  FILE_TOO_LARGE: `El archivo es demasiado grande. Máximo ${
    MAX_FILE_SIZE / (1024 * 1024)
  }MB permitido.`,
  COPY_ERROR: 'No se pudo copiar al portapapeles',
  LOAD_BANKS_ERROR: 'Error al cargar cuentas bancarias',
};

export const SUCCESS_MESSAGES = {
  REQUEST_CREATED: 'Solicitud creada exitosamente',
};

export const AGENT_CREATE_ANIMATION = [
  trigger('slideDown', [
    transition(':enter', [
      style({
        height: '0',
        opacity: '0',
        overflow: 'hidden',
        transform: 'translateY(-10px)',
      }),
      animate(
        '300ms ease-out',
        style({
          height: '*',
          opacity: '1',
          transform: 'translateY(0)',
        }),
      ),
    ]),
    transition(':leave', [
      style({
        height: '*',
        opacity: '1',
        overflow: 'hidden',
        transform: 'translateY(0)',
      }),
      animate(
        '300ms ease-in',
        style({
          height: '0',
          opacity: '0',
          transform: 'translateY(-10px)',
        }),
      ),
    ]),
  ]),
];

// ============================================================
// FINANCIAL MODULE - CONSTANTS
// Icons, colors, and configuration
// ============================================================

// ------------------------------------------------------------
// COMPANY ICONS & COLORS
// ------------------------------------------------------------

export const COMPANY_CONFIG = {
  [Company.SORTI]: {
    icon: 'fa-solid fa-clover',
    color: '#22C55E', // green
    bgColor: '#DCFCE7',
    label: 'Sorti',
  },
  [Company.ZGAMES]: {
    icon: 'fa-solid fa-z',
    color: '#F59E0B', // amber
    bgColor: '#FEF3C7',
    label: 'ZGames',
  },
};

// ------------------------------------------------------------
// STATE COLORS & CONFIG
// ------------------------------------------------------------

export const STATE_CONFIG = {
  [FinancialState.PENDING]: {
    label: 'Pendiente',
    icon: 'fa-regular fa-clock',
    bgColor: '#FEF3C7',
    textColor: '#92400E',
    borderColor: '#F59E0B',
    badgeClass: 'badge-pending',
  },
  [FinancialState.APROVE]: {
    label: 'Aprobado',
    icon: 'fa-solid fa-check',
    bgColor: '#D1FAE5',
    textColor: '#065F46',
    borderColor: '#10B981',
    badgeClass: 'badge-approved',
  },
  [FinancialState.REJECT]: {
    label: 'Rechazado',
    icon: 'fa-solid fa-xmark',
    bgColor: '#FEE2E2',
    textColor: '#991B1B',
    borderColor: '#EF4444',
    badgeClass: 'badge-rejected',
  },
  [FinancialState.PAY]: {
    label: 'Pagado',
    icon: 'fa-solid fa-dollar-sign',
    bgColor: '#DBEAFE',
    textColor: '#1E40AF',
    borderColor: '#3B82F6',
    badgeClass: 'badge-paid',
  },
};

// ------------------------------------------------------------
// MOVEMENT TYPE CONFIG
// ------------------------------------------------------------

export const MOVEMENT_TYPE_CONFIG = {
  [FinancialMovementType.FACTURA]: {
    label: 'Factura',
    icon: 'fa-solid fa-file-invoice',
    bgColor: '#DBEAFE',
    textColor: '#1E40AF',
    badgeClass: 'badge-invoice',
  },
  [FinancialMovementType.GASTO]: {
    label: 'Gasto',
    icon: 'fa-solid fa-receipt',
    bgColor: '#F3E8FF',
    textColor: '#6B21A8',
    badgeClass: 'badge-expense',
  },
};

// ------------------------------------------------------------
// CHART COLORS
// ------------------------------------------------------------

export const CHART_COLORS = {
  pending: '#F59E0B',
  approved: '#22C55E',
  rejected: '#EF4444',
  paid: '#3B82F6',
};

// ------------------------------------------------------------
// TABLE CONFIG
// ------------------------------------------------------------

export const TABLE_PAGE_SIZES = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 10;

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  fill: ApexFill;
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  markers: ApexMarkers;
  colors: string[];
  responsive: ApexResponsive[];
};

export type RadialChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  plotOptions: ApexPlotOptions;
  stroke: ApexStroke;
  fill: ApexFill;
  legend: ApexLegend;
  responsive: ApexResponsive[];
};

export type FilterType =
  | 'all'
  | 'pendiente'
  | 'aprobado'
  | 'rechazado'
  | 'pagado';

export type MetricCardVariant = 'primary' | 'success';
export const BULLET_COLORS: string[] = ['#3b82f6', '#22c55e', '#f59e0b'];
export const COLORS_DONUT = {
  pendiente: '#F59E0B',
  aprobado: '#22C55E',
  rechazado: '#EF4444',
  pagado: '#3B82F6',
};

export const AVATAR_COLORS = [
  '#3B82F6',
  '#22C55E',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
];

export const CHART_CONFIG = {
  DEFAULT_LABELS_WEEKLY: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
  DEFAULT_LABELS_MONTHLY: ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'],
  EMPTY_SERIES_WEEKLY: [0, 0, 0, 0, 0, 0, 0],
  EMPTY_SERIES_MONTHLY: [0, 0, 0, 0],
  FONT_FAMILY: 'Rubik, sans-serif',
  FONT_SIZE: '11px',
  COLORS: {
    LABEL: '#9ca3af',
    GRID: '#f3f4f6',
  },
  HEIGHT: {
    DEFAULT: 180,
    MOBILE: 150,
  },
  BREAKPOINT_MOBILE: 576,
} as const;

export const DEFAULT_AVATAR_COLOR = '#9ca3af';

export type PeriodType = 'weekly' | 'monthly';

export type ChartMoneyFlowOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  fill: ApexFill;
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  markers: ApexMarkers;
  colors: string[];
  responsive: ApexResponsive[];
};

export const FINANCIAL_STATES = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
  PAID: 3,
} as const;

export const FINANCIAL_STATE_LABELS: Record<number, string> = {
  [FINANCIAL_STATES.PENDING]: 'Pendiente',
  [FINANCIAL_STATES.APPROVED]: 'Aprobado',
  [FINANCIAL_STATES.REJECTED]: 'Rechazado',
  [FINANCIAL_STATES.PAID]: 'Pagado',
};

export const FINANCIAL_STATE_COLORS: Record<number, string> = {
  [FINANCIAL_STATES.PENDING]: '#f59e0b',
  [FINANCIAL_STATES.APPROVED]: '#10b981',
  [FINANCIAL_STATES.REJECTED]: '#ef4444',
  [FINANCIAL_STATES.PAID]: '#3b82f6',
};

export const COMPANIES = {
  SORTI: 1,
  ZGAMES: 2,
} as const;

export const COMPANIES_LABELS: Record<number, string> = {
  [COMPANIES.SORTI]: 'Sorti',
  [COMPANIES.ZGAMES]: 'ZGames',
};

// Opciones de IVA para Ecuador
export const IVA_OPTIONS = [
  { value: 0, label: '0%' },
  { value: 5, label: '5%' },
  { value: 12, label: '12%' },
  { value: 15, label: '15%' },
];

// País por defecto (Ecuador)
export const DEFAULT_COUNTRY_ID = 1;

export const getWizardSteps = (): WizardStep[] => [
  {
    index: 0,
    title: 'Información',
    icon: 'fa-solid fa-file-lines',
    isValid: false,
    isActive: true,
    isCompleted: false,
  },
  {
    index: 1,
    title: 'Gastos',
    icon: 'fa-solid fa-receipt',
    isValid: false,
    isActive: false,
    isCompleted: false,
  },
  {
    index: 2,
    title: 'Info Bancaria',
    icon: 'fa-solid fa-building-columns',
    isValid: false,
    isActive: false,
    isCompleted: false,
  },
  {
    index: 3,
    title: 'Documentos',
    icon: 'fa-solid fa-cloud-arrow-up',
    isValid: false,
    isActive: false,
    isCompleted: false,
  },
  {
    index: 4,
    title: 'Resumen',
    icon: 'fa-solid fa-clipboard-check',
    isValid: true,
    isActive: false,
    isCompleted: false,
  },
];

export type ExportFormat = 'pdf' | 'excel';

export const INITIAL_STATS: FinancialStats = {
  totalMovimientos: '$0.00',
  totalImpuestos: '$0.00',
  pendientes: 0,
  aprobados: 0,
  rechazados: 0,
  pagados: 0,
  total: 0,
};

export const NETWORK_QUALITY = {
  GOOD: 'good',
  INTERMITTENT: 'intermittent',
} as const;

export type NetworkQuality =
  (typeof NETWORK_QUALITY)[keyof typeof NETWORK_QUALITY];

export const HEARTBEAT_URL = '/assets/i18n/es.json';
export const HEARTBEAT_TIMEOUT_MS = 5000;
export const INTERMITTENT_LATENCY_MS = 500;
export const NETWORK_HEARTBEAT = {
  RESET_FAILURE_COUNT: 0,
  FIRST_FAILURE_COUNT: 1,
  OFFLINE_FAILURE_COUNT: 2,
  FAILURE_INCREMENT: 1,
  CHECK_INTERVAL_MS: 15000,
} as const;
export const NETWORK_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
};

export const OFFLINE_GAME_STATE = {
  IDLE: 'idle',
  AIMING: 'aiming',
  SHOOTING: 'shooting',
  GOAL: 'goal',
  SAVED: 'saved',
  POST: 'post',
} as const;

export type OfflineGameState =
  (typeof OFFLINE_GAME_STATE)[keyof typeof OFFLINE_GAME_STATE];

export const OFFLINE_SHOOT_ZONES = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const;

export type OfflineShootZone = (typeof OFFLINE_SHOOT_ZONES)[number];

export const OFFLINE_GAME_INITIAL_STATS = {
  shots: 0,
  goals: 0,
  saves: 0,
  streak: 0,
  bestStreak: 0,
} as const;

export const OFFLINE_ZONE_LABELS: Record<OfflineShootZone, string> = {
  'top-left': '↖',
  'top-center': '⬆',
  'top-right': '↗',
  'bottom-left': '↙',
  'bottom-center': '⬇',
  'bottom-right': '↘',
};

export const OFFLINE_ZONE_ADJACENCY: Record<
  OfflineShootZone,
  OfflineShootZone[]
> = {
  'top-left': ['top-center', 'bottom-left'],
  'top-center': ['top-left', 'top-right'],
  'top-right': ['top-center', 'bottom-right'],
  'bottom-left': ['top-left', 'bottom-center'],
  'bottom-center': ['bottom-left', 'bottom-right'],
  'bottom-right': ['top-right', 'bottom-center'],
};

export const OFFLINE_BALL_TRAJECTORIES: Record<
  OfflineShootZone,
  { x: string; y: string }
> = {
  'top-left': { x: '-90px', y: '-160px' },
  'top-center': { x: '0px', y: '-175px' },
  'top-right': { x: '90px', y: '-160px' },
  'bottom-left': { x: '-85px', y: '-100px' },
  'bottom-center': { x: '0px', y: '-110px' },
  'bottom-right': { x: '85px', y: '-100px' },
};

export const OFFLINE_GOAL_MESSAGES = [
  '¡GOOOL! 🔥',
  '¡Qué golazo! 💥',
  '¡Imparable! ⚡',
  '¡A la red! 🥅',
  '¡Crack! 🏆',
  '¡Fenomenal! 🌟',
] as const;

export const OFFLINE_SAVED_MESSAGES = [
  '¡Atajada! 🧤',
  '¡El portero la sacó!',
  '¡Al palo! 😤',
  '¡Cerca! Intenta de nuevo',
  '¡Buena atajada!',
  '¡Se le escapó!',
] as const;

export const OFFLINE_RESULT_ICONS = {
  GOAL: '⚽🎉',
  SAVED: '🧤',
} as const;

export const OFFLINE_POWER_BAR_COLORS = {
  HIGH_ACCURACY: '#22c55e',
  MEDIUM_ACCURACY: '#eab308',
  LOW_ACCURACY: '#ef4444',
} as const;

export const OFFLINE_GAME_TIME_MS = {
  RECONNECT_TOAST: 2000,
  SHOT_RESOLUTION: 700,
  NEXT_ROUND_DELAY: 2000,
  POWER_TICK: 30,
} as const;

export const OFFLINE_POWER_RULES = {
  MIN: 0,
  MAX: 100,
  STEP: 3,
  ASCENDING_DIRECTION: 1,
  DESCENDING_DIRECTION: -1,
  SAME_ZONE_BASE: 0.6,
  SAME_ZONE_ACCURACY_BONUS: 0.15,
  ADJACENT_ZONE_BASE: 0.25,
  ADJACENT_ZONE_ACCURACY_BONUS: 0.1,
  EXTREME_LOW: 15,
  EXTREME_HIGH: 95,
  EXTREME_SHOT_BASE: 0.4,
  PERFECT_MIN: 65,
  PERFECT_MAX: 80,
  GOOD_MIN: 50,
  GOOD_MAX: 90,
  OK_MIN: 30,
  OK_MAX: 95,
  PERFECT_ACCURACY: 1,
  GOOD_ACCURACY: 0.7,
  OK_ACCURACY: 0.4,
  LOW_ACCURACY: 0.1,
} as const;

export const OFFLINE_STREAK = {
  SHOW_STREAK_BADGE_MIN: 2,
  SPECIAL_MESSAGE_MIN: 3,
  SPECIAL_MESSAGE_SUFFIX: 'seguidos! 🔥🔥🔥',
} as const;

export const COMMON_WORDS = [
  'banco',
  'general',
  'cooperativa',
  'de',
  'del',
  'el',
  'la',
  'los',
  'las',
  'nacional',
];
export const CONTROL_KEYS = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab',
  'Enter',
  'Home',
  'End',
];
export const STATUS_FILTER = [
  { label: 'Pendiente', value: 30 },
  { label: 'Aprobado', value: 31 },
  { label: 'Rechazado', value: 32 },
];

export const AMOUNT_OPTIONS = [
  { label: 'Igual', value: 'Igual' },
  { label: 'Menor', value: 'Menor' },
  { label: 'Menor o igual', value: 'Menor o igual' },
  { label: 'Mayor', value: 'Mayor' },
  { label: 'Mayor o igual', value: 'Mayor o igual' },
  { label: 'Rango', value: 'Rango' },
];

export const CLOSURE_PERIODS = {
  DAILY: 'daily',
  MONTHLY: 'monthly',
} as const;

export const CLOSURE_FILTER_STATUS = {
  ALL: 'all',
  PENDING: 'pending',
  SENT: 'sent',
} as const;

export const CLOSURE_ROW_STATUS = {
  SENT: 'Enviado',
  PENDING: 'Pendiente',
} as const;

export const CLOSURE_BULK_ACTION = {
  APP: 'app',
  DIRECT: 'direct',
  ZG: 'zg',
} as const;

export const CLOSURE_TYPE = [
  { value: CLOSURE_FILTER_STATUS.ALL, label: 'Todos' },
  { value: CLOSURE_FILTER_STATUS.PENDING, label: 'Pendiente' },
  { value: CLOSURE_FILTER_STATUS.SENT, label: 'Enviado' },
];

export const AMOUNT_CONDITIONAL_RANGE = 'Rango';

export const FORMAT_DATE = 'yyyy-MM-dd';
export const COUNTRY_CODE_US = 'en-US';

export const CLOSURE_FILTER_DEFAULT_TYPE = CLOSURE_FILTER_STATUS.ALL;
export const CLOSURE_FILTER_MAX_MONTHS_RANGE = 2;
export const CLOSURE_FILTER_AGENT_PAGE_SIZE = 10;
export const CLOSURE_FILTER_SEARCH_DEBOUNCE_MS = 250;
export const CLOSURE_FILTER_AGENT_RESET_DELAY_MS = 100;
export const CLOSURE_FILTER_DATE_FORMAT = 'YYYY-MM-DD';
export const FILTER_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DEFAULT_DATE_FOR_API = 'YYYY-MM-DD 12:00:00';
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
export const DEFAULT_DATETIME_FORMATS = {
  START: 'YYYY-MM-DD 00:00:00',
  END: 'YYYY-MM-DD 23:59:59',
};
export const DEBT_STATUS = {
  PROCESSED: 1,
  DELETED: 2,
} as const;
export const DEFAULT_LANGUAGES = {
  EN: 'en-US',
  ES: 'es-ES',
};
export const TYPE_BANK = {
  CLIENT: 'client',
  ENTERPRISE: 'enterprise',
};
export const NON_NAME_KEYWORDS = [
  'pago',
  'pagaste',
  'transferencia',
  'transaccion',
  'recarga',
  'retiro',
  'banco',
  'codigo',
  'fecha',
  'comprobante',
  'referencia',
  'concepto',
  'detalle',
];
export const GENERIC_LABELS = [
  'fecha de pago',
  'fecha pago',
  'fecha transaccion',
  'fecha de transaccion',
  'nro de transaccion',
  'no de transaccion',
  'numero de transaccion',
  'n de transaccion',
  'nro comprobante',
  'numero comprobante',
  'n de comprobante',
  'pagaste a',
  'pago a',
];
export const DETAIL_NORMALIZE = {
  DEUNA_RECEIPT: 'comprobante de una',
  PICHINCHA: 'pichincha',
  PEIGO: 'peigo',
};

export const INITIAL_VALUES_MINI_DASHBOARD = {
  totalCount: 0,
  totalAmount: 0,
  debitCount: 0,
  debitAmount: 0,
  creditCount: 0,
  creditAmount: 0,
};

export const HOLDERS_CACHE = 20_000;

export const DEFAULT_ATTEMPS = 1;

export const BALANCE_COLUMN_KEY_MAPPING: Record<string, string> = {
  actions: 'Actions',
  date: 'Date',
  agent: 'Agent',
  detail: 'Detail',
  lastBalance: 'LastBalance',
  actualBalance: 'ActualBalance',
  balance: 'Balance',
  percent: 'Percent',
  result: 'Result',
  drag: 'Drag',
  lastBet: 'LastBet',
  bet: 'Bet',
  ggrLastBalance: 'GGRLastBalance',
  ggrActualBalance: 'GGRActualBalance',
  ggrCasinoBalance: 'GGRCasinoBalance',
  ggrResult: 'GGRResult',
  betResult: 'BetResult',
  ref1: 'Ref1',
  ref2: 'Ref2',
  ref3: 'Ref3',
  referredTotal: 'ReferredTotal',
  debtAdjustment: 'DebtAdjustment',
  agentTotal: 'AgentTotal',
  percentEmpresa: 'PercentEmpresa',
  companyProfit: 'CompanyProfit',
  companyTotal: 'CompanyTotal',
  netPercent: 'NetPercent',
};

 export const DUPLICATE_VOUCHER_REJECT_REASON =
    'Solicitud rechazada por comprobante repetido';

export const STATUS_ACTION  = {
  Activate: 'Activar',
  Deactivate: 'Desactivar',
}

export const MONTHS_NUMBER_JAVASCRIPT = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
} as const;

export const MONTH_PAD_LENGTH = 2;

export const MONTH_PAD_FILL = '0';

export const BALANCE_STREAM_PAGE_SIZE = 100;

export const AGENT_PAGE_SIZE = 20;

export const HTTP_STATUS = {
  NETWORK_ERROR: 0,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

export const ABORT_ERROR_NAME = 'AbortError';

export const UNKNOWN_ERROR_MSG = 'Error desconocido';

export const SSE_DATA_PREFIX = 'data: ';

export const PAGE_RANGE_DELTA = 2;

export const SKELETON_ROW_INDEXES: ReadonlyArray<number> = [1, 2, 3, 4, 5];
export const AGENT_PREVIEW_LIMIT = 3;
export const DETAIL_PREVIEW_LIMIT = 20;

export const GEMINI_WARNING_ACTIONS = {
  MANUAL: 'manual',
}

export const GEMINI_EXTRACTION_TYPES = {
  PARTIAL: 'PARTIAL_EXTRACTION'
}

export const USER_DEFAULT_SORTI = 'Sistema';
